import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ListChecks, Plus, Download } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TasksPage({ user }) {
  const [tasks, setTasks] = useState([]);
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddTask, setShowAddTask] = useState(false);
  const [newTask, setNewTask] = useState({
    task_type: 'putaway',
    item_id: '',
    quantity: '',
    from_location_id: '',
    to_location_id: '',
    assigned_to: '',
    priority: 'normal'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('warehouse_token');
      const [tasksRes, itemsRes, locationsRes] = await Promise.all([
        axios.get(`${API}/tasks/details`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/items`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/locations`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setTasks(tasksRes.data);
      setItems(itemsRes.data);
      setLocations(locationsRes.data);
    } catch (error) {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTask = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('warehouse_token');
      const taskData = {
        ...newTask,
        quantity: parseInt(newTask.quantity)
      };
      if (!taskData.assigned_to) delete taskData.assigned_to;
      if (!taskData.from_location_id) delete taskData.from_location_id;
      if (!taskData.to_location_id) delete taskData.to_location_id;

      await axios.post(`${API}/tasks`, taskData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Task created successfully');
      setShowAddTask(false);
      setNewTask({ task_type: 'putaway', item_id: '', quantity: '', from_location_id: '', to_location_id: '', assigned_to: '', priority: 'normal' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create task');
    }
  };

  if (loading) {
    return <div className="p-8">Loading...</div>;
  }

  const pendingTasks = tasks.filter(t => t.status === 'pending');
  const completedTasks = tasks.filter(t => t.status === 'completed');

  return (
    <div className="p-8" data-testid="tasks-page">
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="heading text-3xl font-black text-[#09090B] mb-2" data-testid="tasks-title">
            Task Management
          </h1>
          <p className="text-[#52525B]">Create and manage warehouse tasks</p>
        </div>
        {(user.role === 'admin' || user.role === 'manager') && (
          <div className="flex gap-2">
            <button
              onClick={async () => {
                try {
                  const token = localStorage.getItem('warehouse_token');
                  const response = await axios.get(`${API}/tasks/export`, {
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: 'blob'
                  });
                  const url = window.URL.createObjectURL(new Blob([response.data]));
                  const link = document.createElement('a');
                  link.href = url;
                  const filename = `movement_history_${new Date().toISOString().slice(0,10).replace(/-/g,'')}.csv`;
                  link.setAttribute('download', filename);
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  window.URL.revokeObjectURL(url);
                  toast.success('Movement history exported');
                } catch (error) {
                  toast.error('Failed to export tasks');
                }
              }}
              className="btn-secondary flex items-center gap-2"
              data-testid="export-tasks-button"
            >
              <Download size={18} weight="bold" />
              Export CSV
            </button>
            <button
              onClick={() => setShowAddTask(true)}
              className="btn-primary flex items-center gap-2"
              data-testid="add-task-button"
            >
              <Plus size={18} weight="bold" />
              Create Task
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="card">
          <p className="text-sm text-[#52525B] mb-1">Total Tasks</p>
          <p className="heading text-3xl font-black text-[#09090B]" data-testid="tasks-total">{tasks.length}</p>
        </div>
        <div className="card bg-[#F59E0B]/5 border-[#F59E0B]/20">
          <p className="text-sm text-[#52525B] mb-1">Pending</p>
          <p className="heading text-3xl font-black text-[#09090B]" data-testid="tasks-pending">{pendingTasks.length}</p>
        </div>
        <div className="card bg-[#16A34A]/5 border-[#16A34A]/20">
          <p className="text-sm text-[#52525B] mb-1">Completed</p>
          <p className="heading text-3xl font-black text-[#09090B]" data-testid="tasks-completed">{completedTasks.length}</p>
        </div>
      </div>

      {tasks.length === 0 ? (
        <div className="card text-center py-12">
          <ListChecks size={48} className="mx-auto text-[#52525B] mb-4" weight="duotone" />
          <h3 className="heading text-xl font-bold text-[#09090B] mb-2">No tasks yet</h3>
          <p className="text-[#52525B]">Create your first task to get started</p>
        </div>
      ) : (
        <div className="card">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="tasks-table">
              <thead>
                <tr className="border-b border-[#E4E4E7]">
                  <th className="text-left p-3 text-sm font-medium text-[#52525B]">Type</th>
                  <th className="text-left p-3 text-sm font-medium text-[#52525B]">Item</th>
                  <th className="text-left p-3 text-sm font-medium text-[#52525B]">Quantity</th>
                  <th className="text-left p-3 text-sm font-medium text-[#52525B]">From</th>
                  <th className="text-left p-3 text-sm font-medium text-[#52525B]">To</th>
                  <th className="text-left p-3 text-sm font-medium text-[#52525B]">Priority</th>
                  <th className="text-left p-3 text-sm font-medium text-[#52525B]">Status</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((task) => (
                  <tr key={task.id} className="border-b border-[#E4E4E7] even:bg-[#FAFAFA]" data-testid="task-row">
                    <td className="p-3 text-sm capitalize text-[#09090B]" data-testid="task-type">{task.task_type}</td>
                    <td className="p-3" data-testid="task-item">
                      <p className="mono text-sm font-bold text-[#09090B]">{task.item?.sku}</p>
                      <p className="text-xs text-[#52525B]">{task.item?.name}</p>
                    </td>
                    <td className="p-3 mono text-sm font-bold text-[#09090B]" data-testid="task-qty">{task.quantity}</td>
                    <td className="p-3 mono text-sm text-[#52525B]" data-testid="task-from">{task.from_location?.code || '-'}</td>
                    <td className="p-3 mono text-sm text-[#52525B]" data-testid="task-to">{task.to_location?.code || '-'}</td>
                    <td className="p-3" data-testid="task-priority">
                      <span className={`px-2 py-1 text-xs font-medium rounded-sm capitalize ${
                        task.priority === 'high' ? 'bg-[#DC2626] text-white' :
                        task.priority === 'normal' ? 'bg-[#F59E0B] text-white' :
                        'bg-[#52525B] text-white'
                      }`}>
                        {task.priority}
                      </span>
                    </td>
                    <td className="p-3" data-testid="task-status">
                      <span className={`px-2 py-1 text-xs font-medium rounded-sm capitalize ${
                        task.status === 'completed' ? 'bg-[#16A34A] text-white' :
                        task.status === 'pending' ? 'bg-[#F59E0B] text-white' :
                        'bg-[#52525B] text-white'
                      }`}>
                        {task.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={showAddTask} onOpenChange={setShowAddTask}>
        <DialogContent data-testid="add-task-dialog">
          <DialogHeader>
            <DialogTitle className="heading text-xl font-bold">Create New Task</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddTask} className="space-y-4">
            <div>
              <Label htmlFor="type">Task Type *</Label>
              <Select value={newTask.task_type} onValueChange={(value) => setNewTask({ ...newTask, task_type: value })}>
                <SelectTrigger data-testid="add-task-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="putaway">Put Away</SelectItem>
                  <SelectItem value="picking">Picking</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="item">Item *</Label>
              <Select value={newTask.item_id} onValueChange={(value) => setNewTask({ ...newTask, item_id: value })}>
                <SelectTrigger data-testid="add-task-item-select">
                  <SelectValue placeholder="Select item" />
                </SelectTrigger>
                <SelectContent>
                  {items.map(item => (
                    <SelectItem key={item.id} value={item.id}>{item.sku} - {item.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="quantity">Quantity *</Label>
              <Input
                id="quantity"
                type="number"
                value={newTask.quantity}
                onChange={(e) => setNewTask({ ...newTask, quantity: e.target.value })}
                placeholder="10"
                required
                data-testid="add-task-quantity-input"
              />
            </div>
            {newTask.task_type === 'picking' && (
              <div>
                <Label htmlFor="from">From Location</Label>
                <Select value={newTask.from_location_id} onValueChange={(value) => setNewTask({ ...newTask, from_location_id: value })}>
                  <SelectTrigger data-testid="add-task-from-select">
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map(location => (
                      <SelectItem key={location.id} value={location.id}>{location.code}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {newTask.task_type === 'putaway' && (
              <div>
                <Label htmlFor="to">To Location</Label>
                <Select value={newTask.to_location_id} onValueChange={(value) => setNewTask({ ...newTask, to_location_id: value })}>
                  <SelectTrigger data-testid="add-task-to-select">
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map(location => (
                      <SelectItem key={location.id} value={location.id}>{location.code}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label htmlFor="priority">Priority *</Label>
              <Select value={newTask.priority} onValueChange={(value) => setNewTask({ ...newTask, priority: value })}>
                <SelectTrigger data-testid="add-task-priority-select">
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-3 pt-4">
              <button type="submit" className="flex-1 btn-primary" data-testid="add-task-submit-button">
                Create Task
              </button>
              <button type="button" onClick={() => setShowAddTask(false)} className="flex-1 btn-secondary" data-testid="add-task-cancel-button">
                Cancel
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
